import React from 'react';
import {
    formatValueToCell, getTotalColumn, NAME,
} from '@shared/tables';
import { METRICS_TYPES } from '@shared/constants/MetricsTypeConstants';
import { getMetricsFormula } from '@shared/constants/dashboard/campaign-table-constants';
import { CAMPAIGN_STATUS } from '@domain/campaigns';
import {
    DEFAULT_COLUMN_WIDTH,
    CAMPAIGN_COLUMN_WIDTH,
    CAMPAIGN_COLUMN_MAX_WIDTH,
    STATUS_COLUMN_MAX_WIDTH,
    STATUS,
    BUDGET_COLUMN_MAX_WIDTH,
    BUDGET,
    METRICS_PREFIX,
} from './campaign-table.constants';
import { CampaignNameField } from './column-components/campaign-name-field/campaign-name-field';
import { CampaignStatusSwitchButton } from './column-components/campaign-status-switch-button/campaign-table-status-switch';
import {
    CampaignHeader, TotalLabel, DailyBudgetHeader,
} from './campaign-table.styles';
import { CampaignBudgetField } from './column-components/campaign-budget-field/campaign-budget-field';


export const COLUMN_PROPS = {
    default: {
        Total: ({ column, rows }) => getTotalColumn(column, rows),
        Cell: ({ value, column }) => formatValueToCell(value, column),
        width: DEFAULT_COLUMN_WIDTH,
    },
    [NAME]: {
        Total: () => (<TotalLabel>Total</TotalLabel>),
        Cell: props => <CampaignNameField {...props} />,
        width: CAMPAIGN_COLUMN_WIDTH,
        minWidth: CAMPAIGN_COLUMN_MAX_WIDTH,
        Header: () => (<CampaignHeader>Campaigns</CampaignHeader>),
    },
    [STATUS]: {
        Total: '',
        Cell: props => <CampaignStatusSwitchButton {...props} />,
        maxWidth: STATUS_COLUMN_MAX_WIDTH,
        Header: 'Status',
    },
    [BUDGET]: {
        Total: ({ column, rows }) => getTotalBudget(column, rows),
        Cell: props => <CampaignBudgetField {...props} />,
        maxWidth: BUDGET_COLUMN_MAX_WIDTH,
        Header: () => (<DailyBudgetHeader>Daily Budget</DailyBudgetHeader>),
    },
};

const sumValueOrZero = value => (value || 0);

const getCalculatedMetrics = (metrics, column, prefix = '') => {
    if (!getMetricsFormula(prefix)[column]) return [];
    const metricFormula = getMetricsFormula(prefix)[column];
    const { dividend, divider, multiplier } = metricFormula;
    const calculatedMetricInCampaigns = metrics.reduce(
        (total, metric) => {
            const tempDividend = sumValueOrZero(total.dividend);
            const tempDivider = sumValueOrZero(total.divider);

            return ({
                ...total,
                dividend: tempDividend + metric[dividend],
                divider: tempDivider + metric[divider],
            });
        }, {},
    );

    return (
        {
            [column]: calculateMetricObtainableThruDivision(
                calculatedMetricInCampaigns, multiplier,
            ),
        }
    );
};

const calculateMetricObtainableThruDivision = ({ dividend, divider }, multiplier) => {
    if (!divider || !dividend) return 0;

    return multiplier * dividend / divider;
};


const sumTotalComparison = (rows, metric) => rows
    .reduce((sum, row) => ((row[`comparison_${metric.value}`])
        ? sum + row[`comparison_${metric.value}`]
        : sum + 0), 0);

const getDiffComparison = (minuend, subtrahend) => minuend - subtrahend;

const getPercentageDiffComparison = (minuend, subtrahend) => (
    (minuend - subtrahend) / subtrahend) * 100;

const getSumMetrics = (metrics, mainValue, comparisonValue, value) => {
    const { minuend, subtrahend } = metrics.reduce(
        (total, metric) => {
            const tempMinuend = sumValueOrZero(total.minuend);
            const tempSubtrahend = sumValueOrZero(total.subtrahend);
            return ({
                ...total,
                minuend: tempMinuend + metric[mainValue],
                subtrahend: tempSubtrahend + metric[comparisonValue],
            });
        }, {},
    );

    return (
        {
            [`${METRICS_PREFIX.diff}${value}`]: getDiffComparison(minuend, subtrahend),
            [`${METRICS_PREFIX.percentage_diff}${value}`]: getPercentageDiffComparison(minuend, subtrahend),
        }
    );
};

export const getTotalComparison = (column, rows, acessor) => {
    const { metric } = column;
    const { type, value } = metric;
    const isString = type === METRICS_TYPES.string;
    const campaigns = rows.map(row => row.original);

    if (isString) return '';

    const mainValue = `${METRICS_PREFIX.main}${value}`;
    const comparisonValue = `${METRICS_PREFIX.comparison}${value}`;

    if (!getMetricsFormula('')[value]) {
        const total = getSumMetrics(campaigns, mainValue, comparisonValue, value);
        return total[acessor];
    }

    const minuend = getCalculatedMetrics(campaigns, mainValue, METRICS_PREFIX.main)[mainValue];
    const subtrahend = getCalculatedMetrics(
        campaigns, comparisonValue, METRICS_PREFIX.comparison,
    )[comparisonValue];

    return column.id === `${METRICS_PREFIX.diff}${value}`
        ? getDiffComparison(minuend, subtrahend)
        : getPercentageDiffComparison(minuend, subtrahend);
};

export const getTotalBudget = (column, rows) => {
    const { metric, hasComparisonPeriod } = column;

    const enabledCampaigns = rows.filter(row => row.values.status === CAMPAIGN_STATUS.ENABLED);

    if (hasComparisonPeriod) {
        return sumTotalComparison(enabledCampaigns
            .map(elemet => elemet.original), metric).toFixed(2);
    }

    return formatValueToCell(enabledCampaigns
        .reduce((sum, row) => sum + (row?.values[metric.value] || 0), 0), column);
};
